/**
 * AP-10 Nachtrag 5, 6.2 — **Versandprotokoll der Exporte.**
 *
 * Wer hat wann welchen Export an wen geschickt, und ist er hinausgegangen?
 * Belege stehen nicht hier, sondern im Verlauf des Belegs.
 *
 * Aufbau nach dem verbindlichen Listen-Standard (`docs/pattern-auswertungsmaske.md`):
 * FilterBar + konfigurierbare, sortier- und verschiebbare Spalten, Spaltenlayout
 * serverseitig je Benutzer (AP-23). Sortiert und gefiltert wird auf dem Server.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronLeft, Settings } from 'lucide-react'
import { MAIL_SOURCE_TYPES, type MailDispatchRow } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDateTime } from '@/lib/format-date'
import { mailApi } from '@/lib/mail/mail.api'
import { usersApi } from '@/lib/users/users.api'
import { pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'

type Zeile = MailDispatchRow

interface ColDef {
  key: string
  label: string
  align?: 'right'
  /** Serverseitig sortierbar — Schlüssel für `sortBy`. */
  sortKey?: string
}

const COLUMN_LAYOUT_KEY = 'columnbar_layout_mail_dispatches'
const DEFAULT_COLUMN_KEYS = ['sentBy', 'sourceType', 'recipients', 'subject', 'transport', 'status']

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortConfig: { key: string; dir: 'asc' | 'desc' } | null
}

function loadColumnState(): ColumnState {
  const parsed = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (parsed?.columnKeys?.length) {
    return { columnKeys: parsed.columnKeys, columnWidths: parsed.columnWidths ?? {}, sortConfig: parsed.sortConfig ?? null }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
}

function groesse(bytes: number): string {
  const format = (n: number) => new Intl.NumberFormat(navigator.language, { maximumFractionDigits: 1 }).format(n)
  return bytes >= 1024 * 1024 ? `${format(bytes / (1024 * 1024))} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export default function MailDispatchLogPage() {
  const { t } = useTranslation()
  // AP-35 Phase K: Der Titel der Projektliste trägt den Begriff des Mandanten.
  const { projectPlural } = useTerminology()

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const [rows, setRows] = useState<Zeile[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; totalPages: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [benutzer, setBenutzer] = useState<{ value: string; label: string }[]>([])
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const ALL_COLUMNS: ColDef[] = useMemo(
    () => [
      { key: 'sentBy', label: t('mailLog.columns.sentBy'), sortKey: 'sentBy' },
      { key: 'sourceType', label: t('mailLog.columns.sourceType'), sortKey: 'sourceType' },
      { key: 'fileName', label: t('mailLog.columns.fileName'), sortKey: 'fileName' },
      { key: 'recipients', label: t('mailLog.columns.recipients') },
      { key: 'subject', label: t('mailLog.columns.subject') },
      { key: 'transport', label: t('mailLog.columns.transport'), sortKey: 'transport' },
      { key: 'senderAddress', label: t('mailLog.columns.senderAddress') },
      { key: 'sizeBytes', label: t('mailLog.columns.sizeBytes'), align: 'right', sortKey: 'sizeBytes' },
      { key: 'status', label: t('mailLog.columns.status') },
    ],
    [t],
  )

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: 'dateFrom', label: t('mailLog.filters.dateFrom'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'dateTo', label: t('mailLog.filters.dateTo'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'sentById', label: t('mailLog.columns.sentBy'), type: 'select', category: 'core', operators: ['is'], options: benutzer },
      {
        key: 'sourceType',
        label: t('mailLog.columns.sourceType'),
        type: 'select',
        category: 'core',
        operators: ['is'],
        options: MAIL_SOURCE_TYPES.map((s) => ({ value: s, label: t(`mailExport.titles.${s}`, { projectPlural }) })),
      },
      {
        key: 'status',
        label: t('mailLog.columns.status'),
        type: 'select',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'SENT', label: t('mailLog.status.SENT') },
          { value: 'FAILED', label: t('mailLog.status.FAILED') },
        ],
      },
    ],
    [t, benutzer, projectPlural],
  )

  const sortConfig = colState.sortConfig

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number, sort: ColumnState['sortConfig']) => {
      setLoading(true)
      setError(null)
      try {
        const params: Record<string, unknown> = { page: currentPage, limit: 50 }
        for (const [key, active] of Object.entries(filters)) {
          if (!active.value) continue
          params[key] = key === 'dateFrom' || key === 'dateTo' ? active.value.slice(0, 10) : active.value
        }
        if (sort) {
          params.sortBy = sort.key
          params.sortDir = sort.dir
        }
        const { data } = await mailApi.listDispatches(params)
        setRows(data.data)
        setMeta(data.meta)
      } catch (err) {
        setError(fehlertext(err, t))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void fetchData(filterValues, page, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortConfig?.key, sortConfig?.dir])

  // Referenzdaten getrennt — ein Fehler hier blockiert die Liste nicht.
  useEffect(() => {
    usersApi
      .list({ limit: 100 })
      .then(({ data }) =>
        setBenutzer(
          data.data.map((u) => ({
            value: u.id,
            label: [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email || '—',
          })),
        ),
      )
      .catch(() => undefined)
  }, [])

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(newValues, 1, sortConfig), 400)
    },
    [fetchData, sortConfig],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string | undefined) => {
    if (!key) return
    updateColState((prev) => {
      const current = prev.sortConfig
      const next = !current || current.key !== key ? { key, dir: 'asc' as const } : current.dir === 'asc' ? { key, dir: 'desc' as const } : null
      return { ...prev, sortConfig: next }
    })
    setPage(1)
  }

  const sortIcon = (key: string) =>
    sortConfig?.key === key ? (
      sortConfig.dir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30" />
    )

  // ── Drag & Drop ───────────────────────────────────────────────────────────
  const handleDragStart = (key: string) => { dragKeyRef.current = key }
  const handleDragOver = (e: React.DragEvent, key: string) => { e.preventDefault(); setDragOverKey(key) }
  const handleDragEnd = () => { dragKeyRef.current = null; setDragOverKey(null) }
  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    setDragOverKey(null)
  }

  // ── Spalten-Resize ────────────────────────────────────────────────────────
  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.target as HTMLElement).closest('th')
    resizeRef.current = { key, startX: e.clientX, startW: th?.getBoundingClientRect().width ?? 120 }
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const newW = Math.max(60, resizeRef.current.startW + ev.clientX - resizeRef.current.startX)
      const k = resizeRef.current.key
      updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [k]: newW } }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys.map((k) => ALL_COLUMNS.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = ALL_COLUMNS.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (row: Zeile, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'sentBy': return row.sentBy?.name ?? '—'
      case 'sourceType': return t(`mailExport.titles.${row.sourceType}`, { projectPlural })
      case 'fileName':
        // Nachtrag 5, 3a: eigene Dateien stehen mit Namen im Tooltip, hier nur gezählt.
        return (
          <span className="text-xs" title={[row.fileName, ...row.extraFiles.map((f) => f.fileName)].join(String.fromCharCode(10))}>
            {row.fileName}
            {row.extraFiles.length > 0 && <span className="ml-1 text-muted-foreground">{t('mailLog.extraFiles', { count: row.extraFiles.length })}</span>}
          </span>
        )
      case 'recipients':
        return (
          <span title={row.recipients.join(', ')}>
            {row.recipients.join(', ')}
            {row.copyToSelf && <span className="ml-1 text-xs text-muted-foreground">({t('mailLog.copyToSelf')})</span>}
          </span>
        )
      case 'subject': return <span className="block max-w-[28rem] truncate" title={row.subject}>{row.subject}</span>
      case 'transport': return t(`mailLog.transport.${row.transport}`)
      case 'senderAddress': return row.senderAddress ?? ''
      case 'sizeBytes': return groesse(row.sizeBytes)
      case 'status':
        return row.error ? (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200" title={row.error}>
            {t('mailLog.status.FAILED')}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
            {t('mailLog.status.SENT')}
          </span>
        )
      default: return null
    }
  }

  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <Link to="/settings" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ChevronLeft size={14} /> {t('mailLog.back')}
          </Link>
          <h1 className="text-3xl font-bold text-foreground">{t('mailLog.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('mailLog.subtitle')}</p>
        </div>
      </div>

      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="mail-dispatches"
        defaultFieldKeys={['dateFrom', 'dateTo', 'status']}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={t('mailLog.configureColumns')}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Fixe erste Spalte: der Zeitpunkt */}
                <th
                  className="relative px-4 py-1.5 text-left font-medium text-foreground"
                  style={colState.columnWidths['sentAt'] ? { width: colState.columnWidths['sentAt'] } : undefined}
                >
                  <button type="button" onClick={() => handleSort('sentAt')} className="flex items-center gap-1 hover:text-primary">
                    {t('mailLog.columns.sentAt')}
                    {sortIcon('sentAt')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary" onMouseDown={(e) => handleResizeStart(e, 'sentAt')} />
                </th>

                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      columnConfigMode ? 'cursor-grab' : ''
                    } ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {col.sortKey && !columnConfigMode ? (
                        <button type="button" onClick={() => handleSort(col.sortKey)} className="flex items-center gap-1 hover:text-primary">
                          {col.label}
                          {sortIcon(col.sortKey)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title={t('mailLog.removeColumn')}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}

                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">{t('mailLog.addColumn')}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">{t('mailLog.empty')}</td></tr>
              )}

              {rows.map((row, idx) => (
                <tr key={row.id} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-muted-foreground">{fmtDateTime(row.sentAt)}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                    {loading ? t('common.loading') : t('mailLog.count', { count: meta?.total ?? rows.length })}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`} />
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>{t('mailLog.page', { page: meta.page, pages: meta.totalPages })}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">‹</button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
